import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApiQuery } from '../../../utils/api'
import { Loading, Error } from '../../Elements/Indicators'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../Elements/PageHeader'
import { CheckBadgeIcon, TrophyIcon } from '@heroicons/react/20/solid'
import { Header2, Header3, Header4, PSmallFaded } from '../../Elements/Type'
import ProjectUserSelector from './ProjectUserSeletor'
import { Card } from '../../Elements/Cards'
import { XProject, JiraUser } from '@jirax/common'

/**
 * Safely extract a message string from an unknown error‐like value.
 * Falls back to a generic error string when no message is present.
 */
const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'message' in err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (err as any).message ?? 'Unknown error'
  }
  return String(err) || 'Unknown error'
}

interface Sprint {
  id: number
  name: string
  state: string
  goal?: string
  startDate: string
  endDate: string
  analysisAvailable: boolean
}

interface Board {
  id: string // id comes from API as string – align with consumer components
  name: string
  sprints: Sprint[]
}

interface Project extends XProject {
  boards: Board[]
  users: JiraUser[] // Updated to use JiraUser from @jirax/common
}

interface RouteParams extends Record<string, string | undefined> {
  projKey: string
}

interface SprintsProps {
  projKey: string
  boardId: string // keep in sync with Board.id type
  sprints: Sprint[]
}

const Sprints: React.FC<SprintsProps> = ({ projKey, boardId, sprints }) => {
  const numSprints = 9
  const limitedSprints = sprints.slice(0, numSprints)

  return (
    <div className='mb-4 grid grid-cols-1 gap-6 pb-4 sm:grid-cols-2 md:grid-cols-3'>
      {limitedSprints.map(sprint => {
        const link = sprint.analysisAvailable
          ? `/projects/${projKey}/boards/${boardId}/sprints/${sprint.id}/analysis`
          : `/projects/${projKey}/boards/${boardId}/sprints/${sprint.id}`
        const winnerLink = `/projects/${projKey}/boards/${boardId}/sprints/${sprint.id}/winner`

        return (
          <Card key={sprint.id} className='col-span-1 grow p-4 relative'>
            {/* Trophy Icon with Link to Winner page */}
            {sprint.analysisAvailable && sprint.state !== 'active' && (
              <div className="absolute bottom-3 right-3 z-10">
                <Link
                  to={winnerLink}
                  className="text-yellow-500 hover:text-yellow-700"
                  aria-label="View sprint winner"
                >
                  <TrophyIcon className="h-5 w-5" aria-hidden="true" />
                </Link>
              </div>
            )}

            {/* Main content link */}
            <Link
              className='block'
              to={link}
            >
              <div>
                <div className='grid grid-cols-3'>
                  <div className='col-span-2'>
                    <Header4 title={sprint.name} />
                  </div>
                  {sprint.analysisAvailable && (
                    <div className='text-right'>
                      <span className='inline-flex items-center rounded-full bg-blue-100 px-3 py-0.5 text-sm font-medium text-blue-800'>
                        <CheckBadgeIcon
                          className='mr-1 h-4 w-4 text-blue-800'
                          aria-hidden='true'
                        />
                        Analysis
                      </span>
                    </div>
                  )}
                </div>
                <PSmallFaded text={sprint.goal || ''} />
                {sprint.state !== 'active' && (
                  <PSmallFaded
                    text={`${new Date(sprint.startDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short'
                    })} to ${new Date(sprint.endDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short'
                    })}`}
                  />
                )}

                {sprint.state === 'active' && (
                  <PSmallFaded
                    text={`Active, started on ${new Date(sprint.startDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short'
                    })}`}
                  />
                )}
              </div>
            </Link>
          </Card>
        )
      })}
    </div>
  )
}

const ProjectDetails: React.FC = () => {
  // All hooks must be called at the top level, before any conditional logic
  const { projKey } = useParams<RouteParams>()
  const navigate = useNavigate()
  
  // Use hasValidKey to control query execution
  const hasValidKey = Boolean(projKey)
  
  // First API query - will only execute if projKey exists
  const {
    isLoading,
    error: isErrorProject,
    data: project
  } = useApiQuery<Project>(['projects', projKey], {
    enabled: hasValidKey
  })

  // Second API query - will only execute if project exists and has boards
  const boardId = project?.boards?.[0]?.id
  const {
    isLoading: isLoadingBoard,
    error: boardError,
    data: board
  } = useApiQuery<Board>(['boards', boardId], { 
    enabled: Boolean(hasValidKey && boardId) 
  })

  // Now we can have conditional logic and early returns
  // Handle undefined projKey
  if (!hasValidKey) {
    return <Error title={'Error Loading Project'} message={'Project key is missing'} />
  }

  /* ------------------------------------------------------------------
   * Early exit: project query finished but returned no data
   * ------------------------------------------------------------------ */
  if (!isLoading && !isErrorProject && !project) {
    return (
      <Error
        title={'Project Not Found'}
        message={'The requested project could not be found.'}
      />
    )
  }

  // Update to use accountId instead of id
  const onChangeUser = (userId: string): void => {
    // Navigate to the new project-scoped user stats route
    navigate(`/projects/${projKey}/users/${userId}/stats`)
  }

  if (isLoading || isLoadingBoard) {
    return <Loading message={`Loading project ${projKey}.`} />
  }

  if (isErrorProject || boardError) {
    return (
      <Error
        title={'Error Loading Projects'}
        message={getErrorMessage(boardError || isErrorProject)}
      />
    )
  }

  // At this point, both project and board are guaranteed to exist
  if (!project || !board) {
    return <Error title={'Error Loading Project'} message={'Project or board data is missing'} />
  }

  const headerConfig = {
    pageTitle: project.name,
    backLink: {
      label: 'Back',
      to: '/projects'
    },
    breadcrumb: [
      {
        label: 'Projects',
        to: '/projects'
      },
      {
        label: 'Project Details'
      }
    ]
  }

  return (
    <>
      <PageHeader config={headerConfig} />
      <main className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <Header2 title={board.name} />
        <Header3 title={'Sprints'} />
        <Sprints
          projKey={project.key}
          boardId={board.id}
          sprints={board.sprints}
        />
        <Header3 title={'Team Members'} />
        <ProjectUserSelector project={project} onChangeUser={onChangeUser} user={null} />
      </main>
    </>
  )
}

export default ProjectDetails
